/**
 * Selection state for an integration's "Review matching clients" step (T4-144), kept free of React so the rules
 * that decide what gets linked are testable on their own. A provider-neutral copy of T4-135's
 * xero_match_review/resolution_state.js: the same rules, with a neutral group shape - `sources` are the
 * integration's records (a QuickBooks customer here; a Xero contact once Xero moves onto this copy). Xero is not
 * changed by T4-144; a follow-up moves it here.
 *
 * Keyed by `group.key`, which the integration supplies: QuickBooks' email is not unique per group.
 *
 * Nothing is pre-selected anywhere in here, deliberately: pre-selecting would let a user confirm by reflex a link
 * nobody chose. A choice carried over a BACK (`carryOverSelection`) is the user's own, kept only while it still fits
 * its group. The backend re-checks every pairing against fresh data regardless; what is here is the explanation,
 * not the guard.
 */
export const groupKey = group => group.key;

/** The pairings a group's current selection has decided - one place, so the badge and the import cannot drift. */
const pairingsFor = (group, chosen) => {
	if (!chosen) return [];

	if (group.kind === 'MANY_CLIENTS') {
		return typeof chosen.clientId === 'number' ? [{sourceId: group.sources[0].id, clientId: chosen.clientId}] : [];
	}

	if (group.kind === 'MANY_SOURCES') {
		return typeof chosen.sourceId === 'string' ? [{sourceId: chosen.sourceId, clientId: group.clients[0].clientId}] : [];
	}

	return group.clients.flatMap(client => {
		const sourceId = (chosen.byClientId || {})[client.clientId];

		return sourceId ? [{sourceId, clientId: client.clientId}] : [];
	});
};

/**
 * Whether a group has nothing left to decide - the card's RESOLVED badge, and the footer's count. A many-to-many
 * needs every client decided, and "do not link this client" is a decision. So is a one-to-many or many-to-one card's
 * "do not link" (T4-144 review round 3): a `null` choice, which links nothing.
 */
export const isGroupResolved = (group, selection) => {
	const chosen = selection[groupKey(group)];

	if (!chosen) return false;

	if (group.kind === 'MANY_TO_MANY') {
		const byClientId = chosen.byClientId || {};
		return group.clients.every(client => byClientId[client.clientId] !== undefined);
	}

	if (group.kind === 'MANY_CLIENTS' && chosen.clientId === null) return true;
	if (group.kind === 'MANY_SOURCES' && chosen.sourceId === null) return true;

	return pairingsFor(group, chosen).length === 1;
};

/** How many cards are finished. The footer's numerator, and the same predicate as the badge. */
export const confirmedCount = (groups, selection) => groups.filter(group => isGroupResolved(group, selection)).length;

/** The name of the other client in this group already holding this source, or null. */
export const sourceTakenBy = (group, selection, sourceId, forClientId) => {
	const byClientId = (selection[groupKey(group)] || {}).byClientId || {};

	const holder = group.clients.find(client => client.clientId !== forClientId && byClientId[client.clientId] === sourceId);

	return holder ? holder.name : null;
};

/**
 * The confirmed pairings. Only links are emitted: "do not link", an unchosen source and an untouched group all
 * mean "not linked". A pairing counts as soon as it is made, not once its whole group is settled.
 */
export const toResolutions = (groups, selection) => groups.flatMap(group => pairingsFor(group, selection[groupKey(group)]));

/**
 * What pressing Import will bring in - T4-135's rule: the records matched to one client without asking, the ones
 * that become new clients, and every pairing confirmed so far. It is zero only when the import would do nothing.
 */
export const importCount = (groups, selection, autoLinkCount, createCount) =>
	(autoLinkCount || 0) + (createCount || 0) + toResolutions(groups, selection).length;

/**
 * The part of a choice that still fits its group, or null. Every id it names must still be in the group - a client
 * among its clients, a source among its sources - or be `null` ("do not link"). A MANY_CLIENTS choice names only the
 * client: the customer it decides is the card's one source, so it fits only while that is the same customer.
 */
const keptChoice = (group, chosen, before) => {
	const hasClient = clientId => group.clients.some(client => String(client.clientId) === String(clientId));
	const hasSource = sourceId => group.sources.some(source => source.id === sourceId);

	if (group.kind === 'MANY_CLIENTS') {
		const sameSource = !!before && before.kind === 'MANY_CLIENTS' && before.sources[0].id === group.sources[0].id;
		const fits = chosen.clientId === null || (typeof chosen.clientId === 'number' && hasClient(chosen.clientId));
		return sameSource && fits ? {clientId: chosen.clientId} : null;
	}

	if (group.kind === 'MANY_SOURCES') {
		const fits = chosen.sourceId === null || (typeof chosen.sourceId === 'string' && hasSource(chosen.sourceId));
		return fits ? {sourceId: chosen.sourceId} : null;
	}

	// Each pairing names its client and its source, so each is kept or dropped on its own.
	const byClientId = {};
	for (const [clientId, sourceId] of Object.entries(chosen.byClientId || {})) {
		if (hasClient(clientId) && (sourceId === null || hasSource(sourceId))) byClientId[clientId] = sourceId;
	}
	return Object.keys(byClientId).length > 0 ? {byClientId} : null;
};

/**
 * The choices that carry over to a new preview's groups (T4-144 review round 3, Amar's gap 3): BACK, then CREATE,
 * previews again, and a pick that still fits its group is kept instead of lost. Kept only under the same group key
 * and only as far as it still fits (`keptChoice`); everything else is dropped, so a card whose group changed opens on
 * NEEDS REVIEW again. `previousGroups` are the groups the selection was made on. Pure: a new object.
 */
export const carryOverSelection = (selection, previousGroups, groups) => {
	const before = new Map(previousGroups.map(group => [groupKey(group), group]));
	const kept = {};
	for (const group of groups) {
		const key = groupKey(group);
		const chosen = selection[key];
		const choice = chosen ? keptChoice(group, chosen, before.get(key)) : null;
		if (choice) kept[key] = choice;
	}
	return kept;
};
